import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatDate, formatNOK } from "@/lib/format";
import { useRemoveSupplierLink, useSetSupplierLink } from "@/hooks/use-freelancer-suppliers";
import type { FreelanceSupplierOverviewRow } from "@/lib/db-types";
import { FreelancerPicker } from "./FreelancerPicker";

/** VIB-2927: én leverandør på /frilansere/leverandorer, med Koble, forslag og «Ikke frilanser». */
export function FreelanceSupplierRow({ row, canLink }: { row: FreelanceSupplierOverviewRow; canLink: boolean }) {
  const setLink = useSetSupplierLink();
  const remove = useRemoveSupplierLink();
  const busy = setLink.isPending || remove.isPending;
  const link = (freelancerId: string | null) =>
    setLink.mutate({ linkId: row.link_id, po_supplier_id: row.po_supplier_id, supplier_name: row.supplier_name, freelancer_id: freelancerId });

  return (
    <tr className="border-b border-border/50 last:border-0 align-top hover:bg-muted/30">
      <td className="px-3 py-2.5">
        <div className="font-medium">{row.supplier_name}</div>
        {row.line_descriptions.length > 0 && (
          <div className="text-xs text-muted-foreground">{row.line_descriptions.join(", ")}</div>
        )}
      </td>
      <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
        {row.voucher_count} bilag · {row.project_count} prosjekt{row.project_count === 1 ? "" : "er"}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">{formatNOK(row.invoiced)}</td>
      <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{row.last_voucher_date ? formatDate(row.last_voucher_date) : "—"}</td>
      <td className="px-3 py-2.5">
        {row.status === "linked" && row.freelancer_id && (
          <Link to={`/frilansere/${row.freelancer_id}`} className="inline-flex items-center gap-1 text-primary hover:underline">
            <SrIcon name="film" className="h-3.5 w-3.5" />
            {row.freelancer_name}
          </Link>
        )}
        {row.status === "not_freelancer" && <span className="text-muted-foreground">Ikke frilanser</span>}
        {row.status === "unlinked" && row.suggested_freelancer_id && (
          <span className="text-muted-foreground">Forslag: {row.suggested_freelancer_name}</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {canLink && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {row.status === "unlinked" && row.suggested_freelancer_id && (
              <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => link(row.suggested_freelancer_id)}>
                Godta forslag
              </Button>
            )}
            {row.status !== "linked" && (
              <FreelancerPicker
                context={[row.supplier_name, ...row.line_descriptions].join(" ")}
                suggestedId={row.suggested_freelancer_id}
                onPick={link}
                trigger={
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} aria-label={`Koble ${row.supplier_name} til frilanser`}>
                    Koble
                  </Button>
                }
              />
            )}
            {row.status === "unlinked" && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={() => link(null)}>
                Ikke frilanser
              </Button>
            )}
            {row.status !== "unlinked" && row.link_id && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                disabled={busy}
                onClick={() => remove.mutate({ linkId: row.link_id!, freelancerId: row.freelancer_id })}
              >
                Angre
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
